import * as React from 'react';

import { connect } from 'react-redux';
import { CallHistoryMethodAction } from 'connected-react-router';

import moment from 'moment';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import AppState from '@webapp/store/state';
import {
  plans,
  productSelector,
  recordLabelPlans,
} from '@webapp/store/products/selectors';
import { getSubscriptionChange } from '@webapp/store/subscription/selectors';

import { Option } from '@webapp/types/common/option';

import { Price } from '@common/types/price/price';
import { Coupon } from '@services/product/types/coupon';
import { Product } from '@services/product/types/product';
import { PlanNames } from '@services/product/types/plan-names';
import { Subscription } from '@services/subscription/types/subscription';
import { PaymentFrequency } from '@services/product/types/payment-frequency';

import PricingCard from './pricing-card';
import Flex from '@components/layout/flex';
import Hint from '@components/basics/hint';
import Button from '@components/actions/button';
import Toggle from '@components/actions/toggle';
import ButtonLoadingCtrl from '../button-loading';
import Typography from '@components/basics/typography';

interface PricingCtrlProps {
  className?: string;
  isRecordLabel?: boolean;
  hideFrequency?: boolean;
  hideFree?: boolean;
  buttonLabel?: React.ReactNode;
  frequency?: 'month' | 'year';
  categories?: string[];
  omit?: string[];
  currentSubscription: Subscription | undefined;
  onSelection: (product: Product, price: Price) => void;
  goBack?: () => CallHistoryMethodAction;
}

interface StateProps {
  products: Product[];
  activeCoupon?: Coupon;
  isMobile: boolean;
}

const PRICING_TYPES: Option<PaymentFrequency>[] = [
  { label: 'Monthly', value: 'month' },
  { label: 'Yearly', value: 'year' },
];

const mapStateToProps = (
  state: AppState,
  props: PricingCtrlProps
): StateProps => {
  const products = props.categories?.length
    ? productSelector(state.products.list, props.categories, props.hideFree)
    : props.isRecordLabel
    ? recordLabelPlans(state.products.list, props.hideFree)
    : plans(state.products.list, props.hideFree);

  return {
    products: products.filter(
      (p) => !(props.omit || []).includes(p.product_key)
    ),
    activeCoupon: state.products.coupons.find((c) => c.isActive),
    isMobile: state.ui.mobile,
  };
};

export const PricingCtrl: React.FC<PricingCtrlProps & StateProps> = ({
  currentSubscription,
  ...props
}) => {
  const [paymentFrequency, setPaymentFrequency] =
    React.useState<PaymentFrequency>('year');
  const [selectedPlan, setSelectedPlan] = React.useState<Product | undefined>(
    undefined
  );

  React.useEffect(() => {
    setPaymentFrequency('year');
  }, [currentSubscription]);

  React.useEffect(() => {
    if (!selectedPlan && currentSubscription?.plan) {
      const product = props.products.find(
        (product) => product.name === currentSubscription?.plan
      );
      setSelectedPlan(product);
    }
  }, [props.products, currentSubscription, selectedPlan]);

  const isPlanExpiring: boolean = currentSubscription
    ? !!currentSubscription.ended_at || !currentSubscription.isActive
    : false;

  const selectPlan = (product: Product) => {
    if (selectedPlan?.name !== product.name) {
      setSelectedPlan(product);
    } else {
      setSelectedPlan(undefined);
    }
  };

  // const getDiscountRatio = (products: Product[]) => {
  //   const product = products.find((p) =>
  //     p.prices.some((price) => !!price.value)
  //   );

  //   const yearlyPrice = product?.prices.find((p) => p.frequency === 'year');
  //   const monthlyPrice = product?.prices.find((p) => p.frequency === 'month');

  //   if (monthlyPrice && yearlyPrice) {
  //     const ratio = 1 - yearlyPrice.value / (monthlyPrice.value * 12);
  //     return Math.ceil(ratio * 100);
  //   }
  // };

  const handleSelection = () => {
    if (selectedPlan) {
      const price = selectedPlan.prices.find(
        (p) => p.frequency === paymentFrequency
      );

      if (!price) {
        return;
      }

      props.onSelection(selectedPlan, price);
    }
  };

  const handleCheckoutDisabled = () => {
    if (
      !selectedPlan ||
      (currentSubscription?.frequency === paymentFrequency &&
        currentSubscription?.plan === selectedPlan?.name &&
        currentSubscription.isActive &&
        !currentSubscription.ended_at)
    ) {
      return true;
    }
  };

  const isDowngrading =
    getSubscriptionChange(
      currentSubscription,
      selectedPlan?.name as PlanNames,
      paymentFrequency
    ) === 'DOWNGRADE';

  const getBtnText = () => {
    if (!selectedPlan) {
      return 'ONBOARDING.PRICING.NEW.START.BTN';
    }
    if (selectedPlan?.name === 'Beginner' && !isDowngrading) {
      return 'ONBOARDING.PRICING.NEW.FREE.BTN';
    }
    if (
      isPlanExpiring &&
      currentSubscription?.plan === selectedPlan?.name &&
      currentSubscription?.plan !== 'Beginner'
    ) {
      return 'ONBOARDING.PRICING.NEW.RENEW.BTN';
    }
    if (isDowngrading) {
      return 'ONBOARDING.PRICING.NEW.DOWNGRADE.BTN';
    }
    return 'ONBOARDING.PRICING.NEW.CHECKOUT.BTN';
  };

  return (
    <div className={props.className}>
      {!props.hideFrequency ? (
        <Flex
          className="actions-wrapper"
          alignItems="center"
          justifyContent="center"
        >
          <div className="toggle-wrapper">
            <Toggle
              className="pricing-toggle"
              options={PRICING_TYPES}
              value={paymentFrequency}
              bigFont
              onChange={(value) => setPaymentFrequency(value)}
            />
          </div>
        </Flex>
      ) : null}
      <TitleWrapper>
        {isDowngrading && (
          <>
            <Typography type="subheader">
              <Translate id="ARTIST_PROFILE.PLANS.DOWNGRADE.TITLE" />
            </Typography>
            <Typography type="title" weight="400">
              <Translate id="ARTIST_PROFILE.PLANS.DOWNGRADE.TEXT" />
            </Typography>
          </>
        )}
      </TitleWrapper>
      <Flex flexDirection="column" gap={3} className="mb-40">
        {props.products
          .filter((product) =>
            product.prices.some((p) => p.frequency === paymentFrequency)
          )
          .reverse()
          .map((product) => (
            <PricingCard
              key={product.id}
              className="mb-10"
              product={product}
              isPlanExpiring={
                currentSubscription?.plan !== 'Beginner' &&
                currentSubscription?.plan === product.name &&
                currentSubscription.frequency === paymentFrequency &&
                isPlanExpiring
              }
              isSelected={
                selectedPlan ? product.name === selectedPlan?.name : false
              }
              hasCurrentPlan={
                currentSubscription?.plan === product.name &&
                currentSubscription.frequency === paymentFrequency &&
                currentSubscription.isActive &&
                !currentSubscription.ended_at
              }
              paymentFrequency={paymentFrequency}
              onClick={() => selectPlan(product)}
              activeCoupon={props.activeCoupon}
            />
          ))}
        {!!currentSubscription && (
          <CustomHint
            // TODO: Integrate date until the user can use the plan
            customTitle={
              <Translate
                id="ONBOARDING.PRICING.BILLING.HINT.TITLE"
                data={{
                  date: moment(currentSubscription.ended_at).format(
                    'D MMM YYYY'
                  ),
                }}
              />
            }
          >
            <Translate id="ONBOARDING.PRICING.BILLING.HINT.TEXT" />
          </CustomHint>
        )}
      </Flex>
      <Flex gap={5} flexDirection={props.isMobile ? 'column' : 'row'}>
        {/* TODO: Integrate Button to cancel */}
        {props.goBack && (
          <Button fullWidth buttonType="primary" onClick={props.goBack}>
            <Translate id="ONBOARDING.DOWNGRADE.BTN.CANCEL" />
          </Button>
        )}
        <ButtonLoadingCtrl
          fullWidth
          buttonType={
            currentSubscription?.plan !== 'Beginner'
              ? 'alert-secondary'
              : 'secondary'
          }
          className="btn"
          isLoading={false}
          onClick={handleSelection}
          disabled={handleCheckoutDisabled()}
        >
          {props.buttonLabel ? (
            props.buttonLabel
          ) : (
            <Translate id={getBtnText()} />
          )}
        </ButtonLoadingCtrl>
      </Flex>
    </div>
  );
};

export default connect(mapStateToProps)(styled(PricingCtrl)`
  padding: 0;

  .btn-step {
    margin: 30px 0;
    display: block;

    @media ${({ theme }) => theme.device.tablet} {
      display: none;
    }
  }

  .actions-wrapper {
    margin-bottom: 20px;

    .toggle-wrapper {
      width: 100%;
      position: relative;

      .badge {
        position: absolute;
        top: 0;
        right: 15px;
        transform: translate(30%, -50%) rotate(-3deg);
        z-index: ${({ theme }) => theme.zIndices.over};
        @media ${({ theme }) => theme.device.laptopS} {
          right: 0;
        }
      }
    }

    @media ${({ theme }) => theme.device.tablet} {
      .toggle-wrapper {
        .badge {
          transform: translate(30%, -50%) rotate(-3deg);
        }
      }
    }

    .title-wrapper {
      display: none;

      @media ${({ theme }) => theme.device.tablet} {
        display: block;
      }
    }
  }
`);

const CustomHint = styled(Hint)`
  margin-top: 0;
  border-radius: 0 0 10px 10px;
`;

const TitleWrapper = styled.div`
  display: grid;
  grid-gap: 10px;
  margin-bottom: 30px;
  min-height: 60px;
`;
